import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useCallbacks } from "../lib/tenant-api";
import { apiFetch } from "../lib/api";
import { formatRelative } from "../lib/format";
import {
  EmptyState,
  ErrorBanner,
  LoadingRow,
} from "../components/EmptyState";

export default function Callbacks() {
  const { data, isLoading, error } = useCallbacks();
  const qc = useQueryClient();
  const complete = useMutation({
    mutationFn: (id: string) =>
      apiFetch(`/api/tenant/callbacks/${id}/complete`, { method: "POST" }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["tenant", "callbacks"] });
      qc.invalidateQueries({ queryKey: ["tenant", "overview"] });
    },
  });

  return (
    <div>
      <div className="mb-4">
        <h1 className="text-2xl font-bold text-slate-900">Callback requests</h1>
        <p className="mt-1 text-sm text-slate-500">
          Customers who asked to be called back. Mark complete once you've reached them.
        </p>
      </div>
      {isLoading && <LoadingRow />}
      {error && <ErrorBanner message={(error as Error).message} />}
      {data && data.length === 0 && (
        <EmptyState
          icon="📞"
          title="No open callbacks"
          hint="Callers who can't book during a chat get added here automatically."
        />
      )}
      {data && data.length > 0 && (
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-500 text-xs uppercase">
              <tr>
                <th className="text-left font-medium px-4 py-2">Name</th>
                <th className="text-left font-medium px-4 py-2">Phone</th>
                <th className="text-left font-medium px-4 py-2">Best time</th>
                <th className="text-left font-medium px-4 py-2">Topic</th>
                <th className="text-left font-medium px-4 py-2">Requested</th>
                <th className="text-right font-medium px-4 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {data.map((c) => {
                const open = c.status === "open";
                return (
                  <tr key={c.id} className="border-t border-slate-100">
                    <td className="px-4 py-3 text-slate-900">{c.name}</td>
                    <td className="px-4 py-3 text-slate-700">{c.phone}</td>
                    <td className="px-4 py-3 text-slate-600">
                      {c.best_time || (
                        <span className="text-slate-400">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-slate-600">
                      {c.topic || <span className="text-slate-400">—</span>}
                    </td>
                    <td className="px-4 py-3 text-slate-500">
                      {formatRelative(c.created_at)}
                    </td>
                    <td className="px-4 py-3 text-right">
                      {open ? (
                        <button
                          onClick={() => complete.mutate(c.id)}
                          disabled={complete.isPending}
                          className="px-3 py-1.5 text-xs font-medium rounded-md bg-brand-600 text-white hover:bg-brand-700 disabled:opacity-50"
                        >
                          Mark complete
                        </button>
                      ) : (
                        <span className="text-xs text-emerald-700">
                          ✓ {c.status}
                        </span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
